// Session PJ2: a survey photo slot on the phone. One tap opens the camera; the photo is queued on the device at once (it
// survives the app being killed underground) and goes up when there is signal. A thumbnail is drawn from the queued file,
// or from the server's own photo route once it has gone (a same-origin URL, never a Drive link).
//
// NO `capture` attribute, deliberately (src/lib/attachmentInputs.test.ts): on an iPhone it forces the camera and hides the
// photo library. With accept="image/*" alone the phone offers Take Photo first, so it is still one tap to the camera.
//
// A photo still on this phone can be retaken or removed. One already sent stays on the server (only the office removes a
// survey photo); retaking it adds the new one, which is the one shown.
import { useEffect, useMemo, useRef, useState } from "react";
import { Camera } from "lucide-react";
import { api, ApiError } from "../../../lib/api";
import type { QueuedPhoto } from "../../../lib/offline/types";
import { isImageFile, prepareImageForUpload } from "../../../lib/imageUpload";
import { queueSurveyPhoto, queuedPhotosForJob, removeQueuedPhoto, retryQueuedPhoto, setQueuedPhotoCaption } from "../../../lib/offline/photosStore";
import { EXTRA_PHOTO_SLOT, phoneMadeId } from "../../../lib/projectSurveyDraft";
import { TextBox } from "./SurveyFields";

export interface SlotPhotos {
  server: { id: string; slotKey: string; caption: string | null }[];
  queued: QueuedPhoto[];
}

function useBlobUrl(blob: Blob | undefined): string | null {
  const url = useMemo(() => {
    if (!blob || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return null;
    try {
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, [blob]);
  useEffect(
    () => () => {
      if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
    },
    [url],
  );
  return url;
}

function Thumb({ queued, serverSrc, alt }: { queued?: QueuedPhoto; serverSrc?: string; alt: string }) {
  const local = useBlobUrl(queued?.blob);
  const src = local ?? serverSrc;
  if (!src) return <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-ele-inset text-xs text-ele-muted">Photo</div>;
  return <img src={src} alt={alt} className="h-20 w-20 rounded-lg object-cover" />;
}

const btn = "min-h-11 rounded-xl border border-ele-card-border px-3 py-2 font-menu text-sm text-ele-strong";

export interface PhotoContext {
  jobId: string;
  surveyId: string;
  surveyLabel: string;
  newLift: boolean;
  onChanged: () => void;
}

async function takeInto(ctx: PhotoContext, slotKey: string, file: File, replacing?: QueuedPhoto): Promise<string | null> {
  if (!isImageFile(file)) return "Only a photo can be added here.";
  let f = file;
  try {
    f = await prepareImageForUpload(file);
  } catch {
    f = file;
  }
  // The camera's own time, when it gave one; never invented.
  const capturedAt = file.lastModified > 0 ? new Date(file.lastModified).toISOString() : undefined;
  await queueSurveyPhoto(
    ctx.jobId,
    { surveyId: ctx.surveyId, slotKey, photoId: phoneMadeId(), surveyLabel: ctx.surveyLabel, ...(ctx.newLift ? { newLift: true } : {}) },
    f,
    f.type || "image/jpeg",
    capturedAt,
  );
  if (replacing) await removeQueuedPhoto(replacing.id);
  return null;
}

/**
 * A photo's caption (Session PJ2F), optional, 300 characters at most. A photo still on this phone takes it into its upload; one
 * the office already holds is changed there (needs signal). The photo's id is the same on the phone and on the server, so which
 * of the two it is is decided at the moment of saving, by looking in the outbox: a photo sent a moment ago is the office's now.
 */
function CaptionField({ ctx, label, queued, sent }: { ctx: PhotoContext; label: string; queued?: QueuedPhoto; sent?: { id: string; caption: string | null } }) {
  const photoId = queued?.target?.photoId ?? sent?.id ?? "";
  const current = queued ? (queued.target?.caption ?? "") : (sent?.caption ?? "");
  const [text, setText] = useState(current);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setText(current);
    setNote(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId]);
  if (!photoId) return null;

  async function save() {
    setBusy(true);
    setNote(null);
    try {
      const fresh = queued ? (await queuedPhotosForJob(ctx.jobId)).find((q) => q.id === queued.id) : undefined;
      if (fresh) {
        const saved = await setQueuedPhotoCaption(fresh, text);
        setNote(saved ? "Caption saved. It goes with the photo." : "The caption could not be saved on this phone. Try again.");
        ctx.onChanged();
        return;
      }
      await api.projectSurveys.setPhotoCaption(ctx.surveyId, photoId, text.trim());
      setNote("Caption saved.");
    } catch (err) {
      setNote(err instanceof ApiError && typeof err.body?.message === "string" ? err.body.message : "Could not save the caption. Try again with signal.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 w-full">
      <TextBox label={`Caption for ${label} (optional)`} hint="For example: door on the left. No customer names." value={text} onChange={(v) => setText((v ?? "").slice(0, 300))} />
      <button type="button" className={`${btn} mt-2`} disabled={busy || text.trim() === current.trim()} onClick={() => void save()}>
        Save caption
      </button>
      {note && <p role="status" className="mt-1 text-xs text-ele-muted">{note}</p>}
    </div>
  );
}

/** One named slot: one photo, retaken or removed while still on this phone. */
export function PhotoSlot({ ctx, slotKey, label, photos }: { ctx: PhotoContext; slotKey: string; label: string; photos: SlotPhotos }) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const queued = photos.queued.filter((p) => p.target?.slotKey === slotKey);
  const latestQueued = queued[queued.length - 1];
  const sent = photos.server.filter((p) => p.slotKey === slotKey);
  const latestSent = sent[sent.length - 1];
  const has = !!latestQueued || !!latestSent;

  async function onFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    try {
      const problem = await takeInto(ctx, slotKey, file, latestQueued && latestQueued.status !== "escalated" ? latestQueued : undefined);
      if (problem) setError(problem);
    } catch {
      setError("That photo could not be saved on this phone. Try again.");
    }
    ctx.onChanged();
  }

  return (
    <div className="mb-3 rounded-xl border border-ele-card-border bg-ele-card p-3" data-photo-slot={slotKey}>
      <p className="mb-2 font-heading text-base text-ele-strong">Photo: {label}</p>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-label={`Take photo: ${label}`}
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {has ? (
        <div className="flex flex-wrap items-center gap-3">
          <Thumb queued={latestQueued} serverSrc={latestSent ? api.projectSurveys.photoUrl(ctx.surveyId, latestSent.id) : undefined} alt={label} />
          <div className="flex flex-col gap-2">
            <p className="text-xs text-ele-muted">
              {latestQueued ? (latestQueued.status === "escalated" ? latestQueued.lastError : "Saved on this phone. Sends when you have signal.") : "Sent to the office."}
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={btn} onClick={() => input.current?.click()}>
                Retake
              </button>
              {latestQueued && (
                <button
                  type="button"
                  className={btn}
                  onClick={() => void removeQueuedPhoto(latestQueued.id).then(ctx.onChanged)}
                >
                  Remove
                </button>
              )}
              {latestQueued?.status === "escalated" && (
                <button type="button" className={btn} onClick={() => void retryQueuedPhoto(latestQueued).then(ctx.onChanged)}>
                  Try again
                </button>
              )}
            </div>
          </div>
          <CaptionField ctx={ctx} label={label} queued={latestQueued} sent={latestQueued ? undefined : latestSent} />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex min-h-14 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-ele-card-border font-heading text-base text-ele-strong"
        >
          <Camera className="h-5 w-5" aria-hidden="true" /> Take photo
        </button>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
    </div>
  );
}

/** The "extra" slot: any number of photos that are not in a named slot. */
export function ExtraPhotos({ ctx, photos }: { ctx: PhotoContext; photos: SlotPhotos }) {
  const input = useRef<HTMLInputElement>(null);
  const queued = photos.queued.filter((p) => p.target?.slotKey === EXTRA_PHOTO_SLOT);
  const sent = photos.server.filter((p) => p.slotKey === EXTRA_PHOTO_SLOT);
  return (
    <div className="mb-3 rounded-xl border border-ele-card-border bg-ele-card p-3" data-photo-slot={EXTRA_PHOTO_SLOT}>
      <p className="mb-2 font-heading text-base text-ele-strong">Other photos</p>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-label="Add another photo"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void takeInto(ctx, EXTRA_PHOTO_SLOT, f).finally(ctx.onChanged);
        }}
      />
      {(queued.length > 0 || sent.length > 0) && (
        <div className="mb-2 flex flex-col gap-3">
          {sent.map((p, i) => (
            <div key={p.id} className="flex flex-wrap items-start gap-2">
              <Thumb serverSrc={api.projectSurveys.photoUrl(ctx.surveyId, p.id)} alt="Other photo" />
              <CaptionField ctx={ctx} label={`other photo ${i + 1}`} sent={p} />
            </div>
          ))}
          {queued.map((q, i) => (
            <div key={q.id} className="flex flex-wrap items-start gap-2">
              <div className="flex flex-col items-center gap-1">
                <Thumb queued={q} alt="Other photo" />
                <button type="button" className="min-h-11 text-xs text-ele-muted underline" onClick={() => void removeQueuedPhoto(q.id).then(ctx.onChanged)}>
                  Remove
                </button>
              </div>
              <CaptionField ctx={ctx} label={`other photo ${sent.length + i + 1}`} queued={q} />
            </div>
          ))}
        </div>
      )}
      <button type="button" className={btn} onClick={() => input.current?.click()}>
        Add another photo
      </button>
    </div>
  );
}
